'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Copy-to-clipboard, with a 1.5s confirmation.
 *
 * Both copy mechanisms are tried, because the interesting failure is not the
 * one people design for. `navigator.clipboard` being *absent* (insecure origin,
 * old browser) is easy to test for — but it is far more common for it to be
 * present and then *reject*: the Clipboard API requires the document to be
 * focused and the permission to be granted, so it throws NotAllowedError when
 * the page has lost focus. An `if (clipboard) … else …` therefore takes the
 * modern branch and silently does nothing, which is the one behaviour a copy
 * button cannot have. So: try the API, and fall through to the legacy
 * execCommand path on *rejection*, not merely on absence.
 */
export default function CopyButton({
  text,
  disabled,
  /**
   * What this one copies. The source box has its own copy button now, and two
   * controls on the same screen both announcing "Copy translation" would be a
   * lie to anyone reading the page with a screen reader.
   */
  label = 'Copy translation',
  /**
   * The word dropped, leaving the glyph.
   *
   * For the copy inside the input box, beside Clear at its top right (see
   * Translator), where it sits in the gutter the text is padded away from. A
   * labelled button there would widen that gutter for every line of the box.
   * The confirmation still happens — the glyph turns into the green tick — and
   * the label survives in `aria-label` and the tooltip, so nothing is lost but
   * the width.
   */
  iconOnly = false,
}: { text: string; disabled?: boolean; label?: string; iconOnly?: boolean }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  function legacyCopy(value: string): boolean {
    try {
      const el = document.createElement('textarea');
      el.value = value;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      el.setSelectionRange(0, value.length); // iOS ignores select() alone
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }

  async function copy() {
    if (!text) return;

    let ok = false;
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        ok = true;
      } catch {
        ok = false; // focus/permission — fall through rather than give up
      }
    }
    if (!ok) ok = legacyCopy(text);
    if (!ok) return; // the text is on screen and selectable; nothing useful to say

    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      type="button"
      onClick={copy}
      disabled={disabled || !text}
      aria-label={copied ? 'Copied' : label}
      title={label}
      className={
        'inline-flex items-center rounded-lg text-sm font-medium ' +
        'text-ink-500 transition hover:bg-ink-200/60 hover:text-ink-800 ' +
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent ' +
        'dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100 ' +
        (iconOnly ? 'h-7 w-7 justify-center' : 'gap-1.5 px-2.5 py-1.5')
      }
    >
      {copied ? (
        <>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {!iconOnly && 'Copied'}
        </>
      ) : (
        <>
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7">
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15V5a2 2 0 012-2h10" strokeLinecap="round" />
          </svg>
          {!iconOnly && 'Copy'}
        </>
      )}
    </button>
  );
}
